import React, { useEffect, useState } from "react";
import { Loader2, Sparkles, Trash2, ExternalLink } from "lucide-react";
import api from "../../lib/api";

const inputCls = "w-full bg-white border border-slate-300 rounded-sm p-3 text-base focus:outline-none focus:border-orange-500";

export default function BlogTab() {
  const [posts, setPosts] = useState([]);
  const [topic, setTopic] = useState("");
  const [keywords, setKeywords] = useState("");
  const [audience, setAudience] = useState("both");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [preview, setPreview] = useState(null);

  const load = () => api.get("/admin/marketing/blog").then((r) => setPosts(r.data));
  useEffect(() => {
    load();
  }, []);

  const generate = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/admin/marketing/blog/generate", { topic, keywords: keywords || undefined, audience });
      setTopic("");
      setKeywords("");
      setPreview(data);
      load();
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Generation failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (p) => {
    await api.patch(`/admin/marketing/blog/${p.post_id}`, { status: p.status === "published" ? "draft" : "published" });
    load();
  };
  const remove = async (p) => {
    await api.delete(`/admin/marketing/blog/${p.post_id}`);
    if (preview?.post_id === p.post_id) setPreview(null);
    load();
  };

  return (
    <div data-testid="blog-tab" className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <form onSubmit={generate} className="lg:col-span-4 border border-slate-200 rounded-sm p-6 bg-slate-50 space-y-4 h-fit">
        <h3 className="font-display text-xl font-bold">Generate SEO article</h3>
        <div>
          <label className="text-sm font-medium text-slate-900 mb-1 block">Topic</label>
          <input required value={topic} onChange={(e) => setTopic(e.target.value)} data-testid="blog-topic-input" placeholder="e.g. Plumbing certification for handymen" className={inputCls} />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-900 mb-1 block">Target keywords (optional)</label>
          <input value={keywords} onChange={(e) => setKeywords(e.target.value)} data-testid="blog-keywords-input" placeholder="handyman certification, plumbing basics" className={inputCls} />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-900 mb-1 block">Audience</label>
          <select value={audience} onChange={(e) => setAudience(e.target.value)} data-testid="blog-audience-select" className={inputCls}>
            <option value="both">Both</option>
            <option value="individual handymen">Individual handymen</option>
            <option value="contractors & property managers">Contractors &amp; property managers</option>
          </select>
        </div>
        <button disabled={busy} data-testid="generate-blog-btn" className="w-full inline-flex justify-center items-center gap-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white px-5 py-3 rounded-sm font-semibold">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {busy ? "Writing (30–60s)…" : "Generate article"}
        </button>
        {err && <div className="text-sm text-red-600" data-testid="blog-error">{err}</div>}
      </form>

      <div className="lg:col-span-8 space-y-6">
        <div className="border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">
              <tr>
                <th className="text-left p-3">Article</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.length === 0 && (
                <tr><td colSpan={3} className="p-6 text-slate-500" data-testid="blog-list-empty">No articles yet.</td></tr>
              )}
              {posts.map((p) => (
                <tr key={p.post_id} className="border-t border-slate-200" data-testid={`blog-row-${p.post_id}`}>
                  <td className="p-3">
                    <button onClick={() => setPreview(p)} className="font-semibold text-slate-900 text-left hover:text-orange-600" data-testid={`blog-preview-${p.post_id}`}>{p.title}</button>
                    <div className="text-xs text-slate-500 font-mono">/blog/{p.slug}</div>
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 text-xs font-bold uppercase tracking-[0.15em] rounded-sm ${p.status === "published" ? "bg-green-600 text-white" : "bg-slate-200 text-slate-700"}`}>{p.status}</span>
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button onClick={() => toggle(p)} data-testid={`blog-toggle-${p.post_id}`} className="text-xs font-semibold border border-slate-300 px-2.5 py-1 rounded-sm hover:bg-slate-50 mr-2">
                      {p.status === "published" ? "Unpublish" : "Publish"}
                    </button>
                    {p.status === "published" && (
                      <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="inline-block align-middle mr-2 text-slate-500 hover:text-orange-600" data-testid={`blog-open-${p.post_id}`}><ExternalLink className="w-4 h-4" /></a>
                    )}
                    <button onClick={() => remove(p)} data-testid={`blog-delete-${p.post_id}`} className="text-slate-400 hover:text-red-600 align-middle"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {preview && (
          <div className="border border-slate-200 rounded-sm p-6" data-testid="blog-preview-panel">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Preview · meta: {preview.meta_description}</div>
            <h3 className="font-display text-2xl font-bold mt-2">{preview.title}</h3>
            <div className="mt-2 flex flex-wrap gap-1">{preview.keywords?.map((k) => <span key={k} className="text-xs bg-slate-100 px-2 py-0.5 rounded-sm">{k}</span>)}</div>
            <div className="mt-6 text-slate-800 leading-relaxed [&_h2]:font-display [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mt-6 [&_h3]:font-semibold [&_h3]:mt-4 [&_p]:my-3 [&_ul]:list-disc [&_ul]:pl-6 [&_a]:text-orange-600" dangerouslySetInnerHTML={{ __html: preview.content_html }} />
          </div>
        )}
      </div>
    </div>
  );
}
